import { useState } from "react"
import { FieldArray } from "formik"
import Button from "~/ui-rtk/components/ui/controls/Button"
import { ModelType } from "~/ui-rtk/api/types/custom-attribution.types"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import { WeightFormRow } from "./WeightFormRow"
import {
  HeartIcon,
  MegaphoneIcon,
  UserPlusIcon,
  ArrowPathIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  CubeIcon,
} from "@heroicons/react/24/outline"
import { AttributionSource } from "../constants/attribution"
import {
  getInitiativeTypeColorConfig,
  type CustomInitiativeTypeMeta,
} from "~/ui-rtk/constants/initiative-types"
import { DEFAULT_CUSTOM_ICON } from "~/ui-rtk/constants/initiative-type-colors"

// Brand Value colors (basic pink)
const BRAND_VALUE_COLORS = {
  accent: "text-basic-pink",
  button:
    "bg-basic-pink/20 hover:bg-basic-pink/30 border-basic-pink/30 hover:border-basic-pink/50 text-basic-pink",
  emptyBg: "from-basic-pink/5 to-basic-pink/3 border-basic-pink/50",
  emptyIcon: "bg-basic-pink/10 text-basic-pink",
  tabActive: "text-basic-pink border-b-2 border-basic-pink bg-basic-pink/10",
  tabHover: "hover:text-basic-pink hover:bg-basic-pink/5",
}

interface WeightFormValues {
  weights: Array<{
    id?: string
    modelType: ModelType
    source: string
    metric: string
    weight: number
  }>
}

interface WeightTabsProps {
  values: WeightFormValues
  defaultActiveTab?: ModelType | null
  initiativeType: string
  customTypes?: CustomInitiativeTypeMeta[]
  onWeightChange?: () => void
}

export function WeightTabs({
  values,
  defaultActiveTab,
  initiativeType,
  customTypes,
  onWeightChange,
}: WeightTabsProps) {
  const [activeTab, setActiveTab] = useState<ModelType>(
    defaultActiveTab || ModelType.DIRECT_RESPONSE,
  )

  const directResponseWeights = values.weights.filter(
    w => w.modelType === ModelType.DIRECT_RESPONSE,
  )
  const brandValueWeights = values.weights.filter(
    w => w.modelType === ModelType.BRAND_VALUE,
  )

  const getTabCount = (modelType: ModelType) =>
    values.weights.filter(w => w.modelType === modelType).length

  // Direct-Response colors resolve through the shared palette (predefined family
  // or the custom type's stored color, falling back when unknown).
  const directResponseColors = getInitiativeTypeColorConfig(
    initiativeType,
    customTypes,
  )

  // Get colors based on model type and initiative
  const getColors = (modelType: ModelType) => {
    if (modelType === ModelType.BRAND_VALUE) {
      return BRAND_VALUE_COLORS
    }
    return directResponseColors
  }

  // Get icon based on model type and initiative
  const getIcon = (modelType: ModelType) => {
    if (modelType === ModelType.BRAND_VALUE) {
      return <span className="text-xl">💎</span>
    }

    // Direct Response uses initiative-specific icons
    switch (initiativeType) {
      case InitiativeType.RETENTION:
        return <HeartIcon className="w-5 h-5" />
      case InitiativeType.DEMAND_GENERATION:
        return <MegaphoneIcon className="w-5 h-5" />
      case InitiativeType.DEMAND_GENERATION_2:
        return <MegaphoneIcon className="w-5 h-5" />
      case InitiativeType.DEMAND_CAPTURE_ACQUISITION:
        return <UserPlusIcon className="w-5 h-5" />
      case InitiativeType.DEMAND_CAPTURE_RETARGETING:
        return <ArrowPathIcon className="w-5 h-5" />
      case InitiativeType.DEMAND_CAPTURE_BROAD:
        return <LinkIcon className="w-5 h-5" />
      case InitiativeType.BRANDED_SEARCH:
      case InitiativeType.NON_BRAND_SEARCH:
        return <MagnifyingGlassIcon className="w-5 h-5" />
      case InitiativeType.PMAX:
        return <CubeIcon className="w-5 h-5" />
      default:
        return <DEFAULT_CUSTOM_ICON className="w-5 h-5" />
    }
  }

  return (
    <div className="flex flex-col h-full border border-system-blue-600/30 rounded-lg overflow-hidden bg-system-blue-600/5">
      {/* Tab Headers */}
      <div className="flex border-b border-system-blue-600/30 bg-system-blue-600/10">
        <button
          type="button"
          onClick={() => setActiveTab(ModelType.DIRECT_RESPONSE)}
          className={`flex-1 px-system-md py-system-sm text-base font-medium transition-all ${
            activeTab === ModelType.DIRECT_RESPONSE
              ? getColors(ModelType.DIRECT_RESPONSE).tabActive
              : `text-system-gray-400 ${getColors(ModelType.DIRECT_RESPONSE).tabHover}`
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <span>Direct Response</span>
            {getTabCount(ModelType.DIRECT_RESPONSE) > 0 && (
              <span
                className={`${getColors(ModelType.DIRECT_RESPONSE).emptyIcon} px-system-xs py-0.5 rounded-full text-sm font-medium`}
              >
                {getTabCount(ModelType.DIRECT_RESPONSE)}
              </span>
            )}
          </div>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab(ModelType.BRAND_VALUE)}
          className={`flex-1 px-system-md py-system-sm text-base font-medium transition-all ${
            activeTab === ModelType.BRAND_VALUE
              ? getColors(ModelType.BRAND_VALUE).tabActive
              : `text-system-gray-400 ${getColors(ModelType.BRAND_VALUE).tabHover}`
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <span>Brand Value</span>
            {getTabCount(ModelType.BRAND_VALUE) > 0 && (
              <span
                className={`${getColors(ModelType.BRAND_VALUE).emptyIcon} px-system-xs py-0.5 rounded-full text-sm font-medium`}
              >
                {getTabCount(ModelType.BRAND_VALUE)}
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-system-md overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Tab-specific content */}
          <div className="flex-1 overflow-y-auto space-y-4">
            <FieldArray name="weights">
              {({ push }) => {
                const currentWeights =
                  activeTab === ModelType.DIRECT_RESPONSE
                    ? directResponseWeights
                    : brandValueWeights

                return (
                  <>
                    {currentWeights.length === 0 ? (
                      <div
                        className={`h-full flex flex-col items-center justify-center text-center py-system-2xl bg-gradient-to-br ${getColors(activeTab).emptyBg} border-2 border-dashed rounded-lg`}
                      >
                        <div
                          className={`w-12 h-12 rounded-full ${getColors(activeTab).emptyIcon} flex items-center justify-center mb-system-md`}
                        >
                          <div className={getColors(activeTab).accent}>
                            {getIcon(activeTab)}
                          </div>
                        </div>
                        <div className="text-lg font-medium text-white mb-2">
                          No{" "}
                          {activeTab === ModelType.DIRECT_RESPONSE
                            ? "Direct Response"
                            : "Brand Value"}{" "}
                          weights yet
                        </div>
                        <Button
                          type="button"
                          variant={{ variant: "text" }}
                          onClick={() => {
                            push({
                              modelType: activeTab,
                              source:
                                activeTab === ModelType.BRAND_VALUE
                                  ? AttributionSource.MARATHON
                                  : AttributionSource.GOOGLE_ANALYTICS,
                              metric: "",
                              weight: 0,
                            })
                            onWeightChange?.()
                          }}
                          className={`${getColors(activeTab).button} px-system-md py-system-xs text-base`}
                        >
                          Add First Weight
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-white/5 rounded-md border border-system-blue-600/20">
                        <table className="min-w-full">
                          <thead className="bg-system-blue-600/20 border-b border-system-blue-600/30">
                            <tr>
                              <th className="px-system-sm py-system-xs text-left text-base font-semibold text-white">
                                Source
                              </th>
                              <th className="px-system-sm py-system-xs text-left text-base font-semibold text-white">
                                Metric
                              </th>
                              <th className="px-system-sm py-system-xs text-left text-base font-semibold text-white">
                                Weight
                              </th>
                              <th className="px-system-sm py-system-xs text-left text-base font-semibold text-white">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-system-blue-600/20">
                            {values.weights
                              .map((_, index) => ({
                                weight: values.weights[index],
                                index,
                              }))
                              .filter(
                                ({ weight }) => weight.modelType === activeTab,
                              )
                              .map(({ index }) => (
                                <WeightFormRow key={index} index={index} />
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )
              }}
            </FieldArray>
          </div>

          {/* Add Button - only show if there are existing weights */}
          {(activeTab === ModelType.DIRECT_RESPONSE
            ? directResponseWeights.length
            : brandValueWeights.length) > 0 && (
            <div className="pt-system-md border-t border-system-blue-600/20">
              <FieldArray name="weights">
                {({ push }) => (
                  <Button
                    type="button"
                    variant={{ variant: "text" }}
                    onClick={() => {
                      push({
                        modelType: activeTab,
                        source:
                          activeTab === ModelType.BRAND_VALUE
                            ? AttributionSource.MARATHON
                            : AttributionSource.GOOGLE_ANALYTICS,
                        metric: "",
                        weight: 0,
                      })
                      onWeightChange?.()
                    }}
                    className={`w-full border border-dashed transition-all p-system-sm rounded-md font-medium text-base ${getColors(activeTab).button}`}
                  >
                    Add Another Weight
                  </Button>
                )}
              </FieldArray>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
